// Production-safe Supabase client configuration
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { config } from '@/config/environment';
import { logger, isExpectedAuthError } from '@/utils/logging';

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

/**
 * Custom fetch wrapper to intercept and handle expected auth errors gracefully
 * This prevents console noise during normal sign-out operations
 */
const customFetch = async (url: RequestInfo | URL, options?: RequestInit) => {
  try {
    const response = await fetch(url, options);
    
    // Intercept 400 errors during sign-out (expected - token invalidation)
    if (!response.ok && response.status === 400) {
      const urlString = typeof url === 'string' ? url : url.toString();
      const isSigningOut = sessionStorage.getItem('signing_out') === 'true';
      const isTokenEndpoint = urlString.includes('token') || urlString.includes('refresh');
      
      if (isSigningOut && isTokenEndpoint) {
        // Expected error during sign-out - log as info, not error
        logger.authEvent('Token invalidated during sign-out (expected)');
        return response; // Return without throwing
      }
    }
    
    return response;
  } catch (error) {
    // Only log unexpected network errors
    if (!isExpectedAuthError(error)) {
      logger.error('Supabase network error', error as Error, {
        component: 'SupabaseClient',
        url: typeof url === 'string' ? url : url.toString()
      });
    }
    throw error;
  }
};

export const supabase = createClient<Database>(config.supabase.url, config.supabase.anonKey, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'supabase.auth.token',
    flowType: 'pkce'
  },
  global: {
    fetch: customFetch
  }
});